import { Typography } from '@remarkable/ark-web';
import { Link, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { useMemo } from 'react';
import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { useFleetMembers } from 'src/api/queries/fleet';
import { Tooltip } from 'src/components';

export function OverviewCompliance({
  componentLocation,
}: {
  componentLocation: string;
}) {
  const members = useFleetMembers();
  const search = useSearch({ strict: false });

  const isComplianceFilterActive = search.compliance?.includes('Non-compliant');

  const numberOfNonCompliantDevices = useMemo(() => {
    if (!members.data?.users) {
      return 0;
    }

    return members.data.users
      .flatMap((user) => user.devices || [])
      .filter((device) => device.policyCompliance?.status === 'nonCompliant')
      .length;
  }, [members.data?.users]);

  return (
    <Tooltip title="View non-compliant devices">
      <Link
        data-cy="non-compliant-devices-count"
        aria-label={`View ${numberOfNonCompliantDevices} non-compliant devices`}
        className={clsx(
          'flex flex-1 flex-col items-start justify-center p-16 text-left hover:bg-paper-2 focus-visible:bg-paper-3 lm:py-16',
          isComplianceFilterActive && 'bg-paper-2'
        )}
        to="/enterprise/devices"
        search={{
          compliance: isComplianceFilterActive ? [] : ['Non-compliant'],
        }}
        onClick={() => {
          tracker.trackEvent(
            new ButtonClicked({
              component_location: componentLocation,
              action: 'filter devices by non-compliance',
              text: 'Devices not compliant',
              link_source: '/enterprise/devices',
            })
          );
        }}
      >
        <Typography
          variant="interface-sm"
          leadingNone
          className="cursor-pointer font-[300]"
          as="label"
          htmlFor="non-compliant-devices"
        >
          Devices not compliant
        </Typography>
        <span
          data-cy="value"
          className={clsx(
            'body-lg font-bold ls:body-2xl ls:font-bold',
            numberOfNonCompliantDevices === 0
              ? 'text-gray-600'
              : 'text-feedback-red-500'
          )}
          id="non-compliant-devices"
        >
          {numberOfNonCompliantDevices}
        </span>
      </Link>
    </Tooltip>
  );
}
